/**
 * Brevo (formerly Sendinblue) Email Marketing Integration
 *
 * Setup Instructions:
 * 1. Create a Brevo account at https://www.brevo.com/
 * 2. Go to SMTP & API > API Keys
 * 3. Create a new API key
 * 4. Add to your .env file: VITE_BREVO_API_KEY=your-api-key
 * 5. Create a contact list in Brevo for the waitlist
 * 6. Add the list ID: VITE_BREVO_WAITLIST_LIST_ID=your-list-id
 */

const BREVO_API_KEY = import.meta.env.VITE_BREVO_API_KEY
const BREVO_WAITLIST_LIST_ID = import.meta.env.VITE_BREVO_WAITLIST_LIST_ID

// Check if Brevo is configured
export const isBrevoConfigured = () => {
  return !!(BREVO_API_KEY && BREVO_WAITLIST_LIST_ID)
}

/**
 * Submit a contact to Brevo
 * @param {Object} data - Contact data
 * @param {string} data.email - Email address (required)
 * @param {string} data.phone - Phone number (optional)
 * @param {string} data.fullName - Full name (optional)
 * @param {string} data.interest - Interest area (optional)
 * @param {string} data.listId - List identifier (optional, defaults to waitlist)
 */
export const submitToBrevo = async (data) => {
  // If Brevo is not configured, silently succeed
  if (!isBrevoConfigured()) {
    console.log('Brevo not configured, skipping email submission')
    return { success: true, skipped: true }
  }

  const { email, phone, fullName, interest } = data

  // Split name into first and last
  const nameParts = (fullName || '').split(' ')
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  const contactData = {
    email,
    attributes: {
      FIRSTNAME: firstName,
      LASTNAME: lastName,
      SMS: phone || '',
      INTEREST: interest || '',
      SOURCE: 'RDC Digital Garage',
      SIGNUP_DATE: new Date().toISOString()
    },
    listIds: [parseInt(BREVO_WAITLIST_LIST_ID)],
    updateEnabled: true // Update if contact already exists
  }

  try {
    const response = await fetch('https://api.brevo.com/v3/contacts', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'api-key': BREVO_API_KEY
      },
      body: JSON.stringify(contactData)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))

      // Contact already exists is not really an error
      if (errorData.code === 'duplicate_parameter') {
        console.log('Contact already exists in Brevo, updating...')
        return { success: true, exists: true }
      }

      throw new Error(errorData.message || `Brevo API error: ${response.status}`)
    }

    const result = await response.json()
    console.log('Brevo contact created:', result)
    return { success: true, data: result }
  } catch (error) {
    console.error('Brevo submission error:', error)
    throw error
  }
}

/**
 * Send a transactional email via Brevo
 * @param {Object} options - Email options
 * @param {string} options.to - Recipient email
 * @param {string} options.templateId - Brevo template ID
 * @param {Object} options.params - Template parameters
 */
export const sendTransactionalEmail = async ({ to, templateId, params }) => {
  if (!BREVO_API_KEY) {
    console.log('Brevo not configured, skipping email')
    return { success: true, skipped: true }
  }

  try {
    const response = await fetch('https://api.brevo.com/v3/smtp/email', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'api-key': BREVO_API_KEY
      },
      body: JSON.stringify({
        to: [{ email: to }],
        templateId: parseInt(templateId),
        params
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Brevo API error: ${response.status}`)
    }

    return { success: true }
  } catch (error) {
    console.error('Brevo email error:', error)
    throw error
  }
}
